import { useState, useEffect } from "react";
import { API_URL } from "../../config/api";
import ActiveIcon from "../../Images/Active.svg";
import completedIcon from "../../Images/Completed.svg";
import AssignmentIcon from "../../Images/Assignment.svg";
import PdfIcon from "../../Images/pdf_icon.svg";
import GradeModal from "./StudentAssignmentGrade";
import { getFileNameFromUrl } from "../../config/Filenameinfo";

export default function Demo() {
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedLesson, setSelectedLesson] = useState("");
  const [apiData, setApiData] = useState(null);
  const [allBatchesData, setAllBatchesData] = useState([]); // Store all batches
  const [demoData, setDemoData] = useState(null);
  const [submissionsData, setSubmissionsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [demoLoading, setDemoLoading] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Fetch default demo context
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("access_token");
        const response = await fetch(`${API_URL}/assignments/default_context`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("default API Response:", result);

        // ✅ FIXED: API returns { data: [...] }
        if (
          result.data &&
          Array.isArray(result.data) &&
          result.data.length > 0
        ) {
          setAllBatchesData(result.data); // Store all batches

          // Set first batch as default
          const firstBatch = result.data[0];
          setApiData(firstBatch);

          // Set default selections from first batch
          if (firstBatch.schedule) {
            setSelectedBatch(firstBatch.schedule.schedule_id.toString());
          }
          if (firstBatch.course) {
            setSelectedCourse(firstBatch.course.course_id.toString());
          }
          if (firstBatch.modules && firstBatch.modules.length > 0) {
            setSelectedModule(firstBatch.modules[0].module_id.toString());
            if (
              firstBatch.modules[0].lessons &&
              firstBatch.modules[0].lessons.length > 0
            ) {
              const firstLessonId =
                firstBatch.modules[0].lessons[0].lesson_id.toString();
              setSelectedLesson(firstLessonId);
            }
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle batch change
  const handleBatchChange = (scheduleId) => {
    setSelectedBatch(scheduleId);

    // Find the selected batch data
    const selectedBatchData = allBatchesData.find(
      (batch) => batch.schedule.schedule_id.toString() === scheduleId,
    );

    if (selectedBatchData) {
      setApiData(selectedBatchData);

      // Update course
      if (selectedBatchData.course) {
        setSelectedCourse(selectedBatchData.course.course_id.toString());
      }

      // Reset module and lesson
      if (selectedBatchData.modules && selectedBatchData.modules.length > 0) {
        setSelectedModule(selectedBatchData.modules[0].module_id.toString());
        if (
          selectedBatchData.modules[0].lessons &&
          selectedBatchData.modules[0].lessons.length > 0
        ) {
          setSelectedLesson(
            selectedBatchData.modules[0].lessons[0].lesson_id.toString(),
          );
        } else {
          setSelectedLesson("");
        }
      } else {
        setSelectedModule("");
        setSelectedLesson("");
      }
    }
  };

  // Fetch demo details when lesson or batch changes
  useEffect(() => {
    const fetchDemoDetails = async () => {
      if (!selectedLesson || !selectedBatch) {
        setDemoData(null);
        setSubmissionsData({});
        return;
      }

      setDemoLoading(true);
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("access_token");
        const response = await fetch(
          `${API_URL}/demo/lesson/${selectedLesson}/schedule/${selectedBatch}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Demo Response:", result);

        // Get the first demo from the data array
        if (result.success && result.data && result.data.length > 0) {
          setDemoData(result.data[0]);
          // Fetch submissions for this demo
          await fetchSubmissions(result.data[0].demo_id);
        } else {
          setDemoData(null);
          setSubmissionsData({});
        }

        setDemoLoading(false);
      } catch (error) {
        console.error("Error fetching demo:", error);
        setDemoData(null);
        setSubmissionsData({});
        setDemoLoading(false);
      }
    };

    fetchDemoDetails();
  }, [selectedLesson, selectedBatch]);

  // Fetch submissions for the demo
  const fetchSubmissions = async (demoId) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("access_token");
      const response = await fetch(
        `${API_URL}/demo/demo_submission/${demoId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        console.log("No submissions found or error fetching");
        setSubmissionsData({});
        return;
      }

      const result = await response.json();
      console.log("Submissions Response:", result);

      // Store submissions by enrollment_id for easy lookup
      if (result.success && result.data && result.data.submissions) {
        const submissionsMap = {};

        // Handle array of submissions
        result.data.submissions.forEach((sub) => {
          submissionsMap[sub.enrollment_id] = sub;
        });

        console.log("Submissions Map:", submissionsMap);
        console.log(
          "Enrollment IDs in submissions:",
          Object.keys(submissionsMap),
        );
        setSubmissionsData(submissionsMap);
      } else {
        setSubmissionsData({});
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
      setSubmissionsData({});
    }
  };

  // Get selected module's lessons
  const getSelectedModuleLessons = () => {
    if (!apiData?.modules || !selectedModule) return [];
    const module = apiData.modules.find(
      (m) => m.module_id.toString() === selectedModule,
    );
    return module?.lessons || [];
  };

  // Get students from API data
  const students = apiData?.students || [];

  // Calculate submission statistics
  const totalStudents = students.length;
  const submittedCount = Object.keys(submissionsData).length;
  const pendingCount = students.length - submittedCount;

  const average =
    totalStudents > 0 ? Math.round((submittedCount / totalStudents) * 100) : 0;

  // ✅ Safe mapping from backend response
  const demoStats = apiData?.course?.demos ?? {};

  const totalDemos = demoStats.total_course ?? 0;
  const activeDemos = demoStats.active ?? 0;
  const completedDemos = demoStats.completed ?? 0;

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-lg text-zinc-800">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Stats Cards - FULLY DYNAMIC */}
      <div className="flex justify-start items-start gap-3.5 mb-6">
        <div className="w-56 h-28 bg-white rounded shadow-sm p-4 flex items-center gap-4">
          <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center">
            <img src={AssignmentIcon} alt="DemoIcon" />
          </div>
          <div className="flex flex-col">
            <div className="text-xs text-stone-900 font-['Roboto']">
              Total Demos
            </div>
            <div className="text-2xl font-bold text-black font-['Inter']">
              {String(totalDemos).padStart(2, "0")}
            </div>
          </div>
        </div>

        <div className="w-56 h-28 bg-white rounded shadow-sm p-4 flex items-center gap-4">
          <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center">
            <img src={completedIcon} alt="completedIcon" />
          </div>
          <div className="flex flex-col">
            <div className="text-xs text-stone-900 font-['Roboto']">
              Completed Demos
            </div>
            <div className="text-2xl font-bold text-black font-['Inter']">
              {String(completedDemos).padStart(2, "0")}
            </div>
          </div>
        </div>

        <div className="w-56 h-28 bg-white rounded shadow-sm p-4 flex items-center gap-4">
          <div className="w-16 h-16 bg-cyan-100 rounded-lg flex items-center justify-center">
            <img src={ActiveIcon} alt="ActiveIcon" />
          </div>
          <div className="flex flex-col">
            <div className="text-xs text-stone-900 font-['Roboto']">
              Active Demos
            </div>
            <div className="text-2xl font-bold text-black font-['Inter']">
              {String(activeDemos).padStart(2, "0")}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-4 mb-6">
        {/* Left Panel - Batch Info */}
        <div className="w-[500px] bg-white rounded border border-gray-200 p-6 relative">
          <div className="absolute top-3.5 right-3.5">
            <div className="px-4 py-1 bg-[#D3F3FF] rounded-full">
              <div className="text-[#4AACC5] text-xs font-['Roboto']">
                Active
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3.5 mt-1">
            {/* Select Batch - FIXED */}
            <div className="flex flex-col items-start">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Select Batch
              </div>
              <select
                value={selectedBatch}
                onChange={(e) => handleBatchChange(e.target.value)}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
              >
                <option value="">Select Batch</option>

                {allBatchesData.map((batch) => (
                  <option
                    key={batch.schedule.schedule_id}
                    value={batch.schedule.schedule_id}
                  >
                    {batch.schedule.location} - {batch.course.title}
                  </option>
                ))}
              </select>
            </div>
            <hr className="border-stone-300" />

            {/* Program */}
            <div className="flex flex-col items-start">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Program
              </div>
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
              >
                <option value="">Select Course</option>
                {apiData?.course && (
                  <option value={apiData.course.course_id}>
                    {apiData.course.title}
                  </option>
                )}
              </select>
            </div>
            <hr className="border-stone-300" />

            {/* Module */}
            <div className="text-left">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Module
              </div>
              <select
                value={selectedModule}
                onChange={(e) => {
                  setSelectedModule(e.target.value);
                  const module = apiData?.modules?.find(
                    (m) => m.module_id.toString() === e.target.value,
                  );
                  if (module?.lessons && module.lessons.length > 0) {
                    setSelectedLesson(module.lessons[0].lesson_id.toString());
                  } else {
                    setSelectedLesson("");
                  }
                }}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
              >
                <option value="">Select Module</option>
                {apiData?.modules?.map((module) => (
                  <option key={module.module_id} value={module.module_id}>
                    {module.title}
                  </option>
                ))}
              </select>
            </div>
            <hr className="border-stone-300" />

            {/* Lesson */}
            <div className="text-left">
              <div className="text-stone-900 text-sm font-['Roboto'] mb-1">
                Lesson
              </div>
              <select
                value={selectedLesson}
                onChange={(e) => setSelectedLesson(e.target.value)}
                className="w-full text-left text-zinc-800 text-sm font-semibold font-['Roboto'] border-none outline-none bg-transparent cursor-pointer"
                disabled={!selectedModule}
              >
                <option value="">Select Lesson</option>
                {getSelectedModuleLessons().map((lesson) => (
                  <option key={lesson.lesson_id} value={lesson.lesson_id}>
                    {lesson.title}
                  </option>
                ))}
              </select>
            </div>
            <hr className="border-stone-300" />
          </div>
        </div>

        {/* Right Panel - Demo Details */}
        <div className="flex-1 bg-white rounded border border-gray-200 p-4 relative">
          {demoLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-zinc-600">Loading demo...</div>
            </div>
          ) : demoData ? (
            <>
              <div className="absolute top-4 right-4">
                <span className="text-stone-900 text-base font-['Roboto']">
                  End Date:{" "}
                </span>
                <span className="text-zinc-800 text-lg font-semibold font-['Roboto']">
                  {demoData?.DemoSchedule?.end_date
                    ? new Date(
                        demoData.DemoSchedule.end_date,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                    : "--"}
                </span>
              </div>

              <div className="text-sm text-zinc-800 font-bold font-['Roboto'] mb-4 mt-8">
                {demoData.title}
              </div>

              <div className="text-sm mb-6">
                <span className="text-zinc-800 font-bold font-['Roboto']">
                  Summary : <br />
                </span>
                <span className="text-zinc-600 font-['Roboto']">
                  {demoData.instructions}
                </span>
              </div>

              <div className="text-sm mb-4">
                <span className="text-zinc-800 font-semibold font-['Roboto']">
                  Points: {demoData.min_points} - {demoData.max_points}
                </span>
                <br />
                <span className="text-zinc-600 font-['Roboto']">
                  Max File Size: {demoData.max_file_size_mb} MB | Supported
                  Types: {demoData.file_supported_types?.toUpperCase() || "N/A"}
                </span>
              </div>

              {demoData.file_url && (
                <>
                  <div className="w-52 h-16 bg-white rounded border border-gray-200 p-2.5 flex items-center gap-3 mb-2">
                    <div className="w-11 h-11 relative">
                      <img src={PdfIcon} alt="icon" />
                    </div>
                    <div className="text-xs text-zinc-600 font-['Roboto']">
                      {getFileNameFromUrl(demoData.file_url)}
                    </div>
                  </div>

                  <a
                    href={demoData.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-cyan-500 font-medium font-['Roboto'] underline"
                  >
                    View Reference File
                  </a>
                </>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-zinc-600">No demo found for this lesson</div>
            </div>
          )}
        </div>
      </div>

      {/* Student Submission Status */}
      <div className="bg-white rounded border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div className="text-lg text-zinc-800 font-semibold font-['Roboto']">
            Student Submission Status
          </div>

          <div className="flex gap-8">
            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {students.length}
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Total Student
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {submittedCount}
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Submitted
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {pendingCount}
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Over Due
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl text-zinc-800 font-semibold font-['Roboto']">
                {average}%
              </div>
              <div className="text-sm text-zinc-800 font-['Roboto']">
                Average
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <button className="px-6 py-2 rounded border border-[#4AACC5] text-[#4AACC5] text-sm font-semibold font-['Roboto']">
                Remainder to Pending Students
              </button>
              <button className="px-6 py-2 bg-[#4AACC5] rounded text-white text-sm font-semibold font-['Roboto']">
                Publish Grades
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Student List Table */}
      <div className="bg-white rounded border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="text-lg text-zinc-800 font-semibold font-['Roboto']">
            Student List
          </div>
          <button className="px-4 py-1.5 bg-white rounded border border-slate-200 flex items-center gap-2">
            <span className="text-sm text-zinc-800 font-medium font-['Roboto']">
              Filters
            </span>
            <svg className="w-3 h-2" fill="currentColor" viewBox="0 0 12 8">
              <path d="M10.293.293a1 1 0 011.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5A1 1 0 011.707.293L6 4.586 10.293.293z" />
            </svg>
          </button>
        </div>

        <div className="overflow-x-auto">
          {students.length > 0 ? (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Student Name ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Student ID ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Submitted On ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Status ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Score (Max {demoData?.max_points || "--"}) ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Feedback ↕
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs text-gray-700 font-semibold font-['Roboto'] border border-slate-200">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => {
                  const submission = submissionsData[student.enrollment_id];
                  const isSubmitted = !!submission;

                  return (
                    <tr
                      key={student.user_id || index}
                      className="bg-white hover:bg-gray-50"
                    >
                      <td className="px-4 py-2.5 border border-slate-200">
                        <div className="flex items-center gap-2">
                          {student.avatar_url ? (
                            <img
                              src={student.avatar_url}
                              alt={student.full_name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-semibold text-cyan-600">
                              {student.full_name?.charAt(0)?.toUpperCase() ||
                                "S"}
                            </div>
                          )}
                          <span className="text-sm text-zinc-800 font-['Roboto']">
                            {student.full_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-zinc-800 font-['Roboto'] border border-slate-200">
                        {student.user_id}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-zinc-800 font-['Roboto'] border border-slate-200">
                        {submission?.submitted_at
                          ? new Date(
                              submission.submitted_at,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "--"}
                      </td>
                      <td className="px-4 py-2.5 border border-slate-200">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs text-white font-['Roboto'] ${
                            isSubmitted ? "bg-green-600" : "bg-yellow-500"
                          }`}
                        >
                          {isSubmitted ? "Submitted" : "Pending"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-zinc-800 font-['Roboto'] border border-slate-200">
                        {submission ? submission.grade : "--"}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-zinc-800 font-['Roboto'] border border-slate-200">
                        {submission?.feedback_status || "—"}
                      </td>
                      <td className="px-4 py-2.5 border border-slate-200">
                        {isSubmitted ? (
                          <button
                            onClick={() => {
                              setSelectedSubmission({
                                submission,
                                student,
                                demo: demoData,
                              });
                              setShowGradeModal(true);
                            }}
                            className="text-sm font-['Roboto'] underline text-cyan-500 bg-transparent border-none cursor-pointer"
                          >
                            View
                          </button>
                        ) : (
                          <button
                            onClick={() =>
                              alert("Send reminder to " + student.full_name)
                            }
                            className="text-sm font-['Roboto'] underline text-red-600 bg-transparent border-none cursor-pointer"
                          >
                            Remind
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No students enrolled in this batch
            </div>
          )}
        </div>
      </div>

      {showGradeModal && selectedSubmission && (
        <GradeModal
          isOpen={showGradeModal}
          onClose={() => setShowGradeModal(false)}
          submission={selectedSubmission.submission}
          student={selectedSubmission.student}
          demo={selectedSubmission.demo}
          type="demo"
        />
      )}
    </div>
  );
}
